import { useEffect, useRef, useState, useCallback } from "react";
import type { AxiosInstance } from "axios";
import {
  UpdateEvent,
  SseConnectionState,
  SseConnectionStats,
} from "../index.js";
import { parseSseBlock } from "../utils/sse.js";

/**
 * Optional request authentication for deployments that do not use browser
 * cookies. The callback is evaluated for each reconnect so expiring bearer
 * tokens can be refreshed by the host application.
 */
export type RealtimeStreamAuthHeaders = () =>
  | Record<string, string>
  | Promise<Record<string, string>>;

export type RealtimeStreamCallbacks = {
  onEvent?: (event: UpdateEvent) => void;
  onStateChange?: (state: SseConnectionState) => void;
  onError?: (error: string) => void;
  getAuthHeaders?: RealtimeStreamAuthHeaders;
};

export type RealtimeOrganisation = number | string;

const CURSOR_STORAGE_PREFIX = "realtime-cursor-v2-";
const MIN_RECONNECT_INTERVAL_MS = 1000;
const MAX_RECONNECT_INTERVAL_MS = 30000;
const RECONNECT_BACKOFF_MULTIPLIER = 1.5;

const cursorStorageKey = (organisation: RealtimeOrganisation) =>
  `${CURSOR_STORAGE_PREFIX}${encodeURIComponent(String(organisation))}`;

export function useRealtimeStream(
  api: AxiosInstance,
  organisationId: RealtimeOrganisation | null,
  callbacks: RealtimeStreamCallbacks = {},
) {
  const [stats, setStats] = useState<SseConnectionStats>({
    state: "disconnected",
    connectedAt: null,
    reconnectAttempts: 0,
    lastError: null,
    lastCursor: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const activeRef = useRef(false);
  const callbacksRef = useRef(callbacks);
  const authHeadersRef = useRef(callbacks.getAuthHeaders);
  callbacksRef.current = callbacks;
  authHeadersRef.current = callbacks.getAuthHeaders;

  const updateStats = useCallback((update: Partial<SseConnectionStats>) => {
    setStats((prev: SseConnectionStats) => ({ ...prev, ...update }));
  }, []);

  const getLastCursor = useCallback((): string | null => {
    if (organisationId == null) return null;
    try {
      return (
        sessionStorage.getItem(cursorStorageKey(organisationId))?.trim() || null
      );
    } catch {
      return null;
    }
  }, [organisationId]);

  const saveLastCursor = useCallback(
    (cursor: string) => {
      if (organisationId != null) {
        try {
          sessionStorage.setItem(cursorStorageKey(organisationId), cursor);
        } catch {
          // Storage is best effort; the live connection remains usable.
        }
      }
      updateStats({ lastCursor: cursor });
    },
    [organisationId, updateStats],
  );

  const clearLastCursor = useCallback(() => {
    if (organisationId != null) {
      try {
        sessionStorage.removeItem(cursorStorageKey(organisationId));
      } catch {
        // Storage is best effort; a reset still starts from the feed head.
      }
    }
    updateStats({ lastCursor: null });
  }, [organisationId, updateStats]);

  const connect = useCallback(async () => {
    if (organisationId == null || !activeRef.current) return;
    if (abortControllerRef.current != null) return;

    const baseUrl = api.defaults.baseURL ?? "";
    const params = new URLSearchParams();
    const cursor = getLastCursor();
    if (cursor != null) params.set("cursor", cursor);
    const query = params.toString();
    const organisationPath = encodeURIComponent(String(organisationId));
    const url = `${baseUrl}/organisations/${organisationPath}/realtime/stream${query ? `?${query}` : ""}`;

    const controller = new AbortController();
    abortControllerRef.current = controller;
    updateStats({ state: "connecting" });
    callbacksRef.current.onStateChange?.("connecting");

    const scheduleReconnect = (immediate = false) => {
      if (!activeRef.current || reconnectTimerRef.current != null) return;

      const delay =
        immediate && reconnectAttemptRef.current === 0
          ? 0
          : Math.min(
              MIN_RECONNECT_INTERVAL_MS *
                Math.pow(
                  RECONNECT_BACKOFF_MULTIPLIER,
                  reconnectAttemptRef.current,
                ),
              MAX_RECONNECT_INTERVAL_MS,
            );
      reconnectAttemptRef.current += 1;
      updateStats({
        state: "reconnecting",
        reconnectAttempts: reconnectAttemptRef.current,
      });
      callbacksRef.current.onStateChange?.("reconnecting");

      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        if (!activeRef.current) return;
        if (abortControllerRef.current != null) {
          scheduleReconnect();
          return;
        }
        void connect();
      }, delay);
    };

    try {
      const headers = new Headers({ Accept: "text/event-stream" });
      const commonHeaders = api.defaults.headers.common as
        | Record<string, unknown>
        | undefined;
      const authorization =
        commonHeaders?.Authorization ?? commonHeaders?.authorization;
      if (typeof authorization === "string" && authorization !== "") {
        headers.set("Authorization", authorization);
      }
      const explicitHeaders = await authHeadersRef.current?.();
      for (const [name, value] of Object.entries(explicitHeaders ?? {})) {
        headers.set(name, value);
      }
      const response = await fetch(url, {
        // Match Axios: cross-origin cookies are opt-in. Bearer-authenticated
        // clients must remain compatible with the API's wildcard CORS origin.
        credentials: api.defaults.withCredentials ? "include" : "same-origin",
        headers,
        signal: controller.signal,
      });

      if (!activeRef.current || controller.signal.aborted) return;
      if (!response.ok || !response.body) {
        if (response.status === 409) {
          clearLastCursor();
          const message = "SSE cursor reset required";
          updateStats({ state: "reconnecting", lastError: message });
          callbacksRef.current.onError?.(message);
          scheduleReconnect(true);
          return;
        }
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      reconnectAttemptRef.current = 0;
      updateStats({
        state: "connected",
        connectedAt: new Date(),
        reconnectAttempts: 0,
        lastError: null,
      });
      callbacksRef.current.onStateChange?.("connected");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let resetRequested = false;

      while (activeRef.current && !controller.signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop() ?? "";

        for (const block of blocks) {
          const parsed = parseSseBlock(block);
          if (parsed.id != null) saveLastCursor(parsed.id);
          if (parsed.event === "reset") {
            clearLastCursor();
            const message = "SSE cursor reset required";
            updateStats({ state: "reconnecting", lastError: message });
            callbacksRef.current.onError?.(message);
            resetRequested = true;
            break;
          }
          if (parsed.data == null) continue;
          try {
            const update = JSON.parse(parsed.data) as UpdateEvent;
            // Queue mode may still emit a numeric sequence in the body. The
            // Stream-primary path always has an opaque SSE id.
            // Pushed alerts ("event: alert") carry no cursor and must not move it.
            if (
              parsed.id == null &&
              parsed.event !== "alert" &&
              Number.isFinite(update.seq)
            ) {
              saveLastCursor(String(update.seq));
            }
            callbacksRef.current.onEvent?.(update);
          } catch (err) {
            callbacksRef.current.onError?.(
              `Failed to parse SSE event: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        }
        if (resetRequested) break;
      }

      if (activeRef.current) {
        scheduleReconnect(resetRequested);
      }
    } catch (err) {
      if ((err as Error).name === "AbortError" || !activeRef.current) return;
      const message = err instanceof Error ? err.message : "Unknown SSE error";
      updateStats({ state: "error", lastError: message });
      callbacksRef.current.onStateChange?.("error");
      callbacksRef.current.onError?.(message);
      scheduleReconnect();
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [
    api,
    organisationId,
    getLastCursor,
    saveLastCursor,
    clearLastCursor,
    updateStats,
  ]);

  const disconnect = useCallback(() => {
    activeRef.current = false;
    if (reconnectTimerRef.current != null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    updateStats({ state: "disconnected" });
    callbacksRef.current.onStateChange?.("disconnected");
  }, [updateStats]);

  useEffect(() => {
    if (organisationId == null) return;
    activeRef.current = true;
    void connect();

    return () => {
      activeRef.current = false;
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, [organisationId, connect]);

  return {
    stats,
    disconnect,
    isConnected: stats.state === "connected",
  };
}
